import { useQuery } from "@tanstack/react-query";
import { fetchClient } from "../../API/fetchClient";
import type { KpisFilters } from "./useGetKpisAnalytics";

export interface CarboneSummaryItem {
  code: string;
  name: string;
  total_co2: number;
  avg_co2: number;
  total_incertitude: number;
  avg_incertitude: number;
  unit: string;
}

export interface CarboneTimeSeriesEntry {
  month: string;
  [code: string]: number | string;
}

export interface CarboneEmpreinteResponse {
  summary: CarboneSummaryItem[];
  time_series: CarboneTimeSeriesEntry[];
}

function makeCarboneHook(endpoint: string, queryKey: string) {
  return (filters: KpisFilters) =>
    useQuery<CarboneEmpreinteResponse, Error>({
      queryKey: [queryKey, filters],
      queryFn: async () => {
        const cleanedFilters: Record<string, unknown> = {};
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            cleanedFilters[key] = value;
          }
        });

        const response = await fetchClient<CarboneEmpreinteResponse>(endpoint, {
          method: "POST",
          body: cleanedFilters,
        });

        if (response.error) {
          throw new Error(`[${queryKey}] ${JSON.stringify(response.error)}`);
        }

        return response.data ?? { summary: [], time_series: [] };
      },
      enabled: !!filters.from_period && !!filters.to_period,
      staleTime: 1000 * 60 * 5,
    });
}

export const useGetEnergieCarboneEmpreinte = makeCarboneHook(
  "/core/analytics/energie-carbone/",
  "energieCarboneEmpreinte"
);

export const useGetElevageCarboneEmpreinte = makeCarboneHook(
  "/core/analytics/elevage-carbone/",
  "elevageCarboneEmpreinte"
);

export const useGetSolCarboneEmpreinte = makeCarboneHook(
  "/core/analytics/sol-carbone/",
  "solCarboneEmpreinte"
);

export const useGetGazFrigoCarboneEmpreinte = makeCarboneHook(
  "/core/analytics/gaz-frigo-carbone/",
  "gazFrigoCarboneEmpreinte"
);

export const useGetTransportCarboneEmpreinte = makeCarboneHook(
  "/core/analytics/transport-carbone/",
  "transportCarboneEmpreinte"
);

export const useGetAchatCarboneEmpreinte = makeCarboneHook(
  "/core/analytics/achat-carbone/",
  "achatCarboneEmpreinte"
);

export const useGetImmobilisationCarboneEmpreinte = makeCarboneHook(
  "/core/analytics/immobilisation-carbone/",
  "immobilisationCarboneEmpreinte"
);

export const useGetDechetsCarboneEmpreinte = makeCarboneHook(
  "/core/analytics/dechets-carbone/",
  "dechetsCarboneEmpreinte"
);

// Helper: sum all total_co2 in a summary array
export function sumSummary(summary: CarboneSummaryItem[] | undefined): number {
  return (summary ?? []).reduce((s, i) => s + i.total_co2, 0);
}

// Helper: get monthly total for a poste by summing all code values per entry
export function getMonthlyTotals(
  timeSeries: CarboneTimeSeriesEntry[] | undefined
): { month: string; total: number }[] {
  return (timeSeries ?? []).map((entry) => ({
    month: entry.month,
    total: Object.entries(entry)
      .filter(([k]) => k !== "month")
      .reduce((s, [, v]) => s + (typeof v === "number" ? v : 0), 0),
  }));
}

// Scope classification for Energie codes:
//   FE_Elec (Direct only) → Scope 2
//   *_Amont codes         → Scope 3
//   everything else       → Scope 1
export function classifyEnergieScope(code: string): 1 | 2 | 3 {
  if (code === "FE_Elec") return 2;
  if (code.endsWith("_Amont")) return 3;
  return 1;
}
